import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Plus, Trash2, Loader, IndianRupee, Receipt, Users as UsersIcon,
  Building, Briefcase, CheckCircle, AlertTriangle, X,
} from 'lucide-react';
import {
  listBusinessUnits, createBusinessUnit, deleteBusinessUnit, updateBusinessUnit,
  addBuMember, removeBuMember, addBuOrg, removeBuOrg,
  listBuCosts, addBuCost, deleteBuCost, getBuPL,
  getOrganisations, getAllUsers,
} from '../services/api';

const role = (() => {
  try {
    const t = localStorage.getItem('admin_token');
    return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role || '';
  } catch { return ''; }
})();
const isSuper = role === 'super_admin';

const PRODUCTS = ['Interviewer', 'HireDesk', 'Interview Copilot'];
const COST_CATEGORIES = ['salary', 'infra', 'tooling', 'travel', 'marketing', 'other'];
const PERIODS = [['all', 'All time'], ['year', 'This year'], ['quarter', 'This quarter'], ['month', 'This month']];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function BusinessUnits() {
  const [bus, setBus] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  // shared lookups (super_admin only fetches /users/list)
  const [allUsers, setAllUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listBusinessUnits();
      setBus(data || []);
      if (data?.length && !selectedId) setSelectedId(data[0].bu_id);
      if (isSuper) {
        const [u, o] = await Promise.all([
          getAllUsers().catch(() => []),
          getOrganisations().catch(() => []),
        ]);
        setAllUsers(u || []); setOrgs(o || []);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load Business Units.');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = bus.find((b) => b.bu_id === selectedId);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-brand-400" /> Business Units
          </h2>
          <p className="text-sm text-ink-300 mt-1">
            Autonomous internal divisions within the platform. Each BU has a head, owned customer
            organizations (the revenue source), a team, and a P&amp;L the BU head is accountable for.
          </p>
        </div>
        {isSuper && (
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400">
            <Plus className="w-4 h-4" /> New BU
          </button>
        )}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</div>}

      {loading ? (
        <p className="text-ink-400 text-sm py-12 text-center">Loading…</p>
      ) : bus.length === 0 ? (
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-10 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-ink-600" />
          <p className="text-ink-400">{isSuper ? 'No Business Units yet. Create the first one above.' : 'No Business Unit assigned to your account yet.'}</p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          {/* List (super_admin) */}
          {isSuper && (
            <aside className="space-y-2">
              {bus.map((b) => (
                <button key={b.bu_id} onClick={() => setSelectedId(b.bu_id)}
                  className={`w-full text-left p-3 rounded-lg border ${
                    selectedId === b.bu_id ? 'bg-brand-500/10 border-brand-500/40' : 'bg-ink-800 border-ink-700 hover:border-ink-600'
                  }`}>
                  <p className="text-sm font-medium text-ink-100">{b.name}</p>
                  <p className="text-xs text-ink-500 mt-0.5">{b.code} · {b.assigned_org_ids?.length || 0} orgs</p>
                </button>
              ))}
            </aside>
          )}

          {/* Detail */}
          <div className={isSuper ? '' : 'lg:col-span-2'}>
            {selected && <BuDetail bu={selected} allUsers={allUsers} orgs={orgs} onReload={load} />}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateBuModal allUsers={allUsers} orgs={orgs}
          onClose={() => setShowCreate(false)}
          onCreated={async (b) => { setShowCreate(false); await load(); setSelectedId(b.bu_id); }} />
      )}
    </div>
  );
}

/* ──────────────────────────── Detail panel ──────────────────────────── */
function BuDetail({ bu, allUsers, orgs, onReload }) {
  const [period, setPeriod] = useState('all');
  const [pl, setPl] = useState(null);
  const [costs, setCosts] = useState([]);
  const [costForm, setCostForm] = useState({ category: 'salary', vendor: '', description: '', amount_inr: '', incurred_at: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const userById = Object.fromEntries((allUsers || []).map((u) => [u.user_id, u]));
  const orgById = Object.fromEntries((orgs || []).map((o) => [o.organisation_id, o]));
  const head = bu.head_user_id ? userById[bu.head_user_id] : null;

  const loadPl = useCallback(async () => {
    try { setPl(await getBuPL(bu.bu_id, period)); } catch (e) { setErr(e.response?.data?.detail || 'PL failed'); }
  }, [bu.bu_id, period]);
  const loadCosts = useCallback(async () => {
    try { setCosts(await listBuCosts(bu.bu_id) || []); } catch {}
  }, [bu.bu_id]);

  useEffect(() => { loadPl(); }, [loadPl]);
  useEffect(() => { loadCosts(); }, [loadCosts]);

  const addCost = async (e) => {
    e.preventDefault(); setErr(''); setMsg(''); setBusy(true);
    try {
      await addBuCost(bu.bu_id, {
        ...costForm,
        amount_inr: Number(costForm.amount_inr) || 0,
        incurred_at: costForm.incurred_at || null,
      });
      setCostForm({ category: 'salary', vendor: '', description: '', amount_inr: '', incurred_at: '' });
      setMsg('Cost entry added.');
      await loadCosts(); await loadPl();
    } catch (e2) { setErr(e2.response?.data?.detail || 'Failed to add cost.'); }
    finally { setBusy(false); }
  };

  const delCost = async (cost_id) => {
    if (!window.confirm('Delete this cost entry?')) return;
    await deleteBuCost(bu.bu_id, cost_id); await loadCosts(); await loadPl();
  };

  const addMember = async (uid) => { await addBuMember(bu.bu_id, uid); onReload(); };
  const removeMember = async (uid) => { await removeBuMember(bu.bu_id, uid); onReload(); };
  const addOrg = async (oid) => { await addBuOrg(bu.bu_id, oid); onReload(); loadPl(); };
  const removeOrg = async (oid) => { await removeBuOrg(bu.bu_id, oid); onReload(); loadPl(); };

  const candidateMembers = (allUsers || []).filter((u) => !(bu.member_user_ids || []).includes(u.user_id) && u.user_id !== bu.head_user_id);
  const candidateOrgs = (orgs || []).filter((o) => o.organisation_id && !(bu.assigned_org_ids || []).includes(o.organisation_id));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs text-ink-500 uppercase tracking-wide font-mono">{bu.code}</p>
            <h3 className="text-xl font-bold text-ink-100 mt-0.5">{bu.name}</h3>
            <p className="text-xs text-ink-400 mt-1">
              Head: <span className="text-ink-200">{head ? `${head.full_name} (${head.email})` : (bu.head_user_id || '— not set —')}</span>
            </p>
            {bu.products?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {bu.products.map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-ink-700 text-ink-300">{p}</span>)}
              </div>
            )}
          </div>
          {isSuper && (
            <button onClick={async () => { if (window.confirm('Delete this BU?')) { await deleteBusinessUnit(bu.bu_id); onReload(); } }}
              className="p-2 rounded-md text-red-400 hover:bg-red-500/10" title="Delete BU">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* P&L */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h4 className="text-sm font-semibold text-ink-100 flex items-center gap-1.5"><IndianRupee className="w-4 h-4 text-brand-400" /> Profit &amp; Loss</h4>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 bg-ink-900 border border-ink-700 rounded-md text-xs text-ink-200">
            {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {!pl ? <p className="text-sm text-ink-500">Loading…</p> : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="bg-ink-900 border border-ink-700 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-ink-400">Revenue</p>
                <p className="mt-1 text-2xl font-bold text-emerald-300">{inr(pl.revenue_inr)}</p>
                <p className="text-xs text-ink-500">{pl.transactions} transactions</p>
              </div>
              <div className="bg-ink-900 border border-ink-700 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wide text-ink-400">Cost</p>
                <p className="mt-1 text-2xl font-bold text-amber-300">{inr(pl.cost_inr)}</p>
                <p className="text-xs text-ink-500">{pl.cost_entries} entries</p>
              </div>
              <div className={`border rounded-lg p-4 ${pl.net_inr >= 0 ? 'bg-emerald-500/[0.06] border-emerald-500/30' : 'bg-red-500/[0.06] border-red-500/30'}`}>
                <p className="text-xs uppercase tracking-wide text-ink-400">Net</p>
                <p className={`mt-1 text-2xl font-bold ${pl.net_inr >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{inr(pl.net_inr)}</p>
                <p className="text-xs text-ink-500">revenue − cost</p>
              </div>
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-400 mb-2">Revenue by org</p>
                {pl.revenue_by_org?.length ? (
                  <table className="w-full text-sm">
                    <tbody>{pl.revenue_by_org.map((r) => (
                      <tr key={r.org_id} className="border-t border-ink-700">
                        <td className="py-1.5 text-ink-200">{r.name}</td>
                        <td className="py-1.5 text-right text-ink-100">{inr(r.inr)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p className="text-xs text-ink-500">No revenue in this period.</p>}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-400 mb-2">Cost by category</p>
                {pl.cost_by_category?.length ? (
                  <table className="w-full text-sm">
                    <tbody>{pl.cost_by_category.map((c) => (
                      <tr key={c.category} className="border-t border-ink-700">
                        <td className="py-1.5 text-ink-200 capitalize">{c.category}</td>
                        <td className="py-1.5 text-right text-ink-100">{inr(c.inr)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <p className="text-xs text-ink-500">No costs in this period.</p>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Cost ledger entry */}
      <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-1.5"><Receipt className="w-4 h-4 text-brand-400" /> Cost ledger</h4>
        <form onSubmit={addCost} className="grid gap-3 sm:grid-cols-[120px_1fr_1fr_120px_140px_auto]">
          <select value={costForm.category} onChange={(e) => setCostForm({ ...costForm, category: e.target.value })}
            className="px-2 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100">
            {COST_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={costForm.vendor} onChange={(e) => setCostForm({ ...costForm, vendor: e.target.value })} placeholder="vendor (optional)"
            className="px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          <input value={costForm.description} onChange={(e) => setCostForm({ ...costForm, description: e.target.value })} placeholder="description"
            className="px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          <input type="number" min={0} step={0.01} value={costForm.amount_inr} onChange={(e) => setCostForm({ ...costForm, amount_inr: e.target.value })} placeholder="₹ amount" required
            className="px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          <input type="date" value={costForm.incurred_at} onChange={(e) => setCostForm({ ...costForm, incurred_at: e.target.value })}
            className="px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          <button type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
            {busy ? <Loader className="w-4 h-4 animate-spin" /> : 'Add'}
          </button>
        </form>
        {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
        {msg && <p className="mt-2 text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {msg}</p>}

        <div className="mt-4 max-h-72 overflow-y-auto">
          {costs.length === 0 ? (
            <p className="text-xs text-ink-500">No cost entries yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-400 text-xs"><th className="text-left py-1">Date</th><th className="text-left py-1">Category</th><th className="text-left py-1">Vendor</th><th className="text-left py-1">Description</th><th className="text-right py-1">Amount</th><th className="w-8" /></tr></thead>
              <tbody>{costs.map((c) => (
                <tr key={c.cost_id} className="border-t border-ink-700">
                  <td className="py-2 text-ink-400 text-xs">{c.incurred_at ? new Date(c.incurred_at).toLocaleDateString() : '—'}</td>
                  <td className="py-2 text-ink-300 capitalize">{c.category}</td>
                  <td className="py-2 text-ink-400">{c.vendor || '—'}</td>
                  <td className="py-2 text-ink-300">{c.description || '—'}</td>
                  <td className="py-2 text-right text-ink-100">{inr(c.amount_inr)}</td>
                  <td className="py-2"><button onClick={() => delCost(c.cost_id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>

      {/* Members + Orgs (super_admin manages; bu_head views) */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-1.5"><UsersIcon className="w-4 h-4 text-brand-400" /> Members</h4>
          <div className="space-y-2">
            {(bu.member_user_ids || []).length === 0 && <p className="text-xs text-ink-500">No members yet.</p>}
            {(bu.member_user_ids || []).map((uid) => {
              const u = userById[uid];
              return (
                <div key={uid} className="flex items-center justify-between bg-ink-900 border border-ink-700 rounded-lg px-3 py-2">
                  <span className="text-sm text-ink-200">{u ? `${u.full_name} · ${u.role}` : uid}</span>
                  {isSuper && <button onClick={() => removeMember(uid)} className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>}
                </div>
              );
            })}
            {isSuper && candidateMembers.length > 0 && (
              <details className="mt-2"><summary className="text-xs text-ink-400 cursor-pointer">Add a member</summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {candidateMembers.slice(0, 30).map((u) => (
                    <button key={u.user_id} onClick={() => addMember(u.user_id)}
                      className="w-full text-left px-3 py-1.5 rounded text-xs text-ink-300 hover:bg-ink-700">
                      {u.full_name} · <span className="text-ink-500">{u.role}</span>
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="bg-ink-800 border border-ink-700 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-1.5"><Building className="w-4 h-4 text-brand-400" /> Assigned organizations</h4>
          <div className="space-y-2">
            {(bu.assigned_org_ids || []).length === 0 && <p className="text-xs text-ink-500">No orgs assigned. (No revenue source for this BU.)</p>}
            {(bu.assigned_org_ids || []).map((oid) => (
              <div key={oid} className="flex items-center justify-between bg-ink-900 border border-ink-700 rounded-lg px-3 py-2">
                <span className="text-sm text-ink-200">{orgById[oid]?.name || oid}</span>
                {isSuper && <button onClick={() => removeOrg(oid)} className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            {isSuper && candidateOrgs.length > 0 && (
              <details className="mt-2"><summary className="text-xs text-ink-400 cursor-pointer">Assign an organization</summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {candidateOrgs.slice(0, 30).map((o) => (
                    <button key={o.organisation_id} onClick={() => addOrg(o.organisation_id)}
                      className="w-full text-left px-3 py-1.5 rounded text-xs text-ink-300 hover:bg-ink-700">{o.name}</button>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Create modal ──────────────────────────── */
function CreateBuModal({ allUsers, orgs, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', code: '', head_user_id: '', products: [], assigned_org_ids: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (key, val) => setForm((f) => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val],
  }));

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setBusy(true);
    try {
      const created = await createBusinessUnit({
        name: form.name.trim(), code: form.code.trim() || null,
        head_user_id: form.head_user_id || null,
        products: form.products,
        assigned_org_ids: form.assigned_org_ids,
      });
      onCreated(created);
    } catch (e2) { setErr(e2.response?.data?.detail || 'Failed to create.'); }
    finally { setBusy(false); }
  };

  const headCandidates = (allUsers || []).filter((u) => u.role === 'bu_head' || u.role === 'client_admin');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-ink-900 border border-ink-700 rounded-xl w-full max-w-lg max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-700">
          <h3 className="text-lg font-semibold text-ink-100">New Business Unit</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus
              className="w-full px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-100" placeholder="e.g. Tech Hiring BU" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Code (auto if blank)</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
              className="w-full px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-100" placeholder="e.g. TECH-BU" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Head (user)</label>
            <select value={form.head_user_id} onChange={(e) => setForm({ ...form, head_user_id: e.target.value })}
              className="w-full px-3 py-2 bg-ink-800 border border-ink-700 rounded-lg text-sm text-ink-100">
              <option value="">— none —</option>
              {headCandidates.map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name} · {u.role}</option>)}
            </select>
            <p className="text-[11px] text-ink-500 mt-1">Tip: create a user with role <code className="text-brand-400">bu_head</code> for clean isolation.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Products owned (informational)</label>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map((p) => (
                <label key={p} className="flex items-center gap-1.5 text-sm text-ink-300 px-3 py-1.5 rounded-md bg-ink-800 border border-ink-700 cursor-pointer">
                  <input type="checkbox" checked={form.products.includes(p)} onChange={() => toggle('products', p)} /> {p}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Initial assigned organizations</label>
            <div className="max-h-36 overflow-y-auto bg-ink-800 border border-ink-700 rounded-lg p-2 space-y-1">
              {(orgs || []).filter((o) => o.organisation_id).map((o) => (
                <label key={o.organisation_id} className="flex items-center gap-2 text-xs text-ink-300 px-2 py-1 rounded hover:bg-ink-700 cursor-pointer">
                  <input type="checkbox" checked={form.assigned_org_ids.includes(o.organisation_id)} onChange={() => toggle('assigned_org_ids', o.organisation_id)} /> {o.name}
                </label>
              ))}
            </div>
          </div>
          {err && <div className="text-sm text-red-400">{err}</div>}
        </form>
        <div className="px-5 py-4 border-t border-ink-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-ink-800 text-ink-200 text-sm hover:bg-ink-700">Cancel</button>
          <button onClick={submit} disabled={busy} className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
            {busy ? <><Loader className="w-4 h-4 animate-spin" /> Creating…</> : 'Create BU'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BusinessUnits;
